import React, { ComponentProps } from 'react';
import { TFunction } from 'react-i18next';
import {
  CatalogEntry,
  LearningPath,
  LiveEvent,
  ModalityExtension,
  type OfferingTypes,
} from 'types';
import { ExamStatus } from 'types/exam';
import { PremiumSessionUserFacingStatus } from 'types/premiumSession';
import { CourseLvcUserFacingStatus } from 'views/Catalog/useCourseLiveClassInfo';
import { OfferingCard } from '@ole-ui/ole-ui-components';
import { LiveVirtualClass as LiveVirtualClassEntry } from 'types/liveClass';
import { Router } from 'router5';
import type CatalogStore from 'stores/Catalog';

export { ModalityExtension, type OfferingTypes };

export type BodyRowProps = ComponentProps<typeof OfferingCard.BodyRow>;

export enum BodyRowType {
  Custom = 'custom',
  Icons = 'icons',
}

export enum IconLabel {
  Duration = 'duration',
  Labs = 'labs',
  Level = 'level',
  Offerings = 'offerings',
  Quizzes = 'quizzes',
  Videos = 'videos',
}

export enum IconType {
  Arrow = 'arrow',
  Eye = 'eye',
}

export type ActionButtonConfig = {
  buttonLabel: string;
  href?: string;
  icon: IconType;
  onClick?: (e?: React.MouseEvent) => void;
};

export type ActionButtonsContext = {
  displayProgress: boolean;
  entry: CatalogEntry;
  hasProgress: boolean;
  isEnrolledSkillPath?: Boolean;
  isLocked: boolean;
  learningPath?: LearningPath;
  liveEventInfo?: LiveEvent;
  lvcInfo: { lvcStatus?: CourseLvcUserFacingStatus; lvcUuid?: string };
  premiumSessionCourseOfferingSlug?: string;
  premiumSessionSlug?: string;
  premiumSessionStatus?: PremiumSessionUserFacingStatus;
  catalogStore: CatalogStore;
  routerStore: {
    router: Router;
    navigate: (route: string, params?: Record<string, unknown>) => void;
  };
  sessionDocId?: string;
  skillsAssessmentId?: string;
  t: TFunction<'translation', undefined>;
};

export type ActionButtonsResult = {
  actionButtonA?: ActionButtonConfig;
  actionButtonB?: ActionButtonConfig;
};

export type CustomRowsContext = {
  attendedSessionDate?: string;
  examExperienceQuota?: ExamExperienceQuota;
  examStatusInfo?: ExamStatusInfo;
  learningPath?: LearningPath;
  liveEventInfo?: LiveEvent;
  liveVirtualClassInfo?: LiveVirtualClassInfo;
  lvcStatus?: CourseLvcUserFacingStatus;
  offeringProducts: string[];
  offeringTopics: string[];
  premiumSessionStatus?: PremiumSessionUserFacingStatus;
  schedulledSessionDate?: string;
  sessionProgress?: SessionProgress;
  t: TFunction<'translation', undefined>;
  userTimezone?: string;
};

export type ExamExperienceQuota = {
  allowed: number;
  used: number;
};

export type ExamStatusInfo = {
  content?: string;
  status: ExamStatus;
};

export type HeaderConfigResult = {
  iconA?: HeaderIconConfig;
  iconB: HeaderIconConfig;
  title: string;
};

export type SkillsAssessmentEntry = {
  modality: ModalityExtension.SkillsAssessment;
  id: string;
  title: string;
  timestamp: string;
  totalScore: number;
};

export type OfferingCardEntry = CatalogEntry | SkillsAssessmentEntry;

export type HeaderContext = {
  premiumSessionStatus?: PremiumSessionUserFacingStatus;
  t: TFunction<'translation', undefined>;
};

export type HeaderIconConfig = {
  backgroundColor: string;
  color?: string;
  iconTitle: string;
  src: string;
};

export type IconRowsContext = {
  hasProgress: boolean;
};

export type LiveVirtualClassInfo = {
  attentedEnrolledLVC?: LiveVirtualClassEntry[];
  lvcAttended?: number;
  lvcQuota?: number;
  ongoingEnrolledLVC?: LiveVirtualClassEntry[];
  upcomingEnrolledLVC?: LiveVirtualClassEntry[];
};

export type LayoutContext = {
  hasSessionProgress?: boolean;
  learningPath?: LearningPath;
  lvcStatus?: CourseLvcUserFacingStatus;
};

export type OfferingTypeConfig = {
  actionButtons: (ctx: ActionButtonsContext) => ActionButtonsResult;
  bodyRowsLayout: (ctx: LayoutContext) => BodyRowType[];
  buildCustomRows?: (ctx: CustomRowsContext) => BodyRowProps[];
  headerConfig: (ctx: HeaderContext) => HeaderConfigResult;
  iconRowsLayout?: (ctx: IconRowsContext) => IconLabel[][];
};

export type SessionProgress = {
  hasSessionProgress: boolean;
  sessionsStatusList: Array<{
    slug: string;
    status: PremiumSessionUserFacingStatus;
  }>;
};

export type TechOverviewDuration = {
  duration: number;
  unit: string;
};
